import type { BigNumber } from "../BigNumber.js";
import type { DisplayMode } from "../types.js";
import { alphabetSymbol } from "./powerOfThousand.js";

/**
 * (자릿수, 약자) 배열을 자릿수 내림차순으로 매칭하는 compact 표기의 공통 기반.
 * 인도식(K/L/Cr)·태국식(หมื่น/แสน/ล้าน)처럼 단위 간 간격이 불규칙한 표기에 사용한다.
 * 내부 1000-base를 log10 기반(`totalDigits = log10|mantissa| + 3·exponent`)으로 rebasing하고,
 * 최대 단위를 초과하면 알파벳(1000-base)으로 fallback한다.
 */
abstract class DigitUnitDisplayModeBase implements DisplayMode {
  /** (자릿수, 약자) — **자릿수 내림차순**. 예: [[7,"Cr"],[5,"L"],[3,"K"]]. */
  protected abstract readonly units: readonly (readonly [number, string])[];

  format(value: BigNumber, decimals: number): string {
    if (value.isZero()) return "0";
    const totalDigits = value.log10();
    const units = this.units;
    const smallest = units.length > 0 ? units[units.length - 1]![0] : 3;
    const sign = value.isNegative() ? -1 : 1;

    // 최소 단위 미만 — 일반 정수.
    if (totalDigits < smallest) return Math.round(value.toNumber()).toString();

    // 자릿수 내림차순 매칭.
    for (const [digits, symbol] of units) {
      if (totalDigits >= digits) {
        return (sign * Math.pow(10, totalDigits - digits)).toFixed(decimals) + symbol;
      }
    }

    // 단위 소진 → 알파벳 1000-base fallback.
    const alphaExp = Math.floor(totalDigits / 3);
    return (sign * Math.pow(10, totalDigits - 3 * alphaExp)).toFixed(decimals) + alphabetSymbol(alphaExp);
  }
}

/**
 * 인도 표기 — K(10^3)/L(lakh,10^5)/Cr(crore,10^7)/Ar(10^9)/Kh(10^11)/Nl/Pd/Sn/Sh(10^19).
 * 처음 K(10^3) 후 매 +2 자릿수마다 단위가 올라간다. 소진 시 알파벳 fallback.
 */
export class IndianDisplayMode extends DigitUnitDisplayModeBase {
  protected readonly units = [
    [19, "Sh"], [17, "Sn"], [15, "Pd"], [13, "Nl"], [11, "Kh"],
    [9, "Ar"], [7, "Cr"], [5, "L"], [3, "K"],
  ] as const;
}

/**
 * 태국 표기 — หมื่น(10^4)/แสน(10^5)/ล้าน(10^6)/ล้านล้าน(10^12).
 * ล้าน(10^6) 위는 별도 단어 없이 ล้าน base 곱으로 합성(10^7 = "10ล้าน"). 10^12 초과는 알파벳 fallback.
 */
export class ThaiDisplayMode extends DigitUnitDisplayModeBase {
  protected readonly units = [
    [12, "ล้านล้าน"], [6, "ล้าน"], [5, "แสน"], [4, "หมื่น"],
  ] as const;
}
